import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock, Fragment as _Fragment, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { class: "flex items-center" }
const _hoisted_2 = { class: "text-subtitle2 ellipsis" }
const _hoisted_3 = {
  key: 0,
  class: "sfx-card__top-right-label"
}
const _hoisted_4 = { class: "text-h4" }

import SfxBtnIcon from "src/components/button/SfxBtnIcon.vue"
import SfxCard from "src/components/card/SfxCard.vue"
import SfxChip from "src/components/data-point/SfxChip.vue"
import SfxDataPoint from "src/components/data-point/SfxDataPoint.vue"
import { useCacheStore } from "src/stores/cacheStore"
import { SFX_CARD_CONDENSED_HEIGHT } from "src/util/constants/configurations"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { getAttachmentContent, getFirstImageAttachment } from "src/util/functions/attachments"
import { getIssuePriorityColor } from "src/util/functions/issuePriority"
import { getAcceptedOrUnreviewedCorrectiveWorkOnItemForIssue } from "src/util/functions/issues"
import { IconRowElement } from "src/util/types/components/sfxIconRow"
import { IssueCardData } from "src/util/types/dataModels/issue"
import { CorrectiveWorkOnItem } from "src/util/types/dataModels/performedWork"
import { computed, PropType, ref, watch } from "vue"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxCardWorkOrderIssueCondensed',
  props: {
  issue: {
    type: Object as PropType<IssueCardData>,
    required: true,
  },

  // If the card has closable set to true, then it would have the X/Remove icon on the top right.
  closable: {
    type: Boolean,
    default: () => false,
  },

  // If performed is true, then add the green border and background, and all additional icons.
  performed: {
    type: Boolean,
    default: () => false,
  },
},
  emits: ["clickClose"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

//endregion

//region Variables

const cacheStore = useCacheStore()

const storeIssue = ref<IssueCardData>()

const overdue = computed(() => (props.issue.overdueTime || 0) > 0)
const workDone = computed(() => props.issue.correctiveWork?.conclusion === "accepted")
const itemDesignation = computed(() => itemRaw.value?.designation)

const iconRow = ref<IconRowElement[]>([])
const workDomainDesignation = computed(() => workDomainRaw.value?.designation)
const issueThumbnail = ref<string | undefined>()

const issueThumbnailRaw = ref(props.issue.thumbnail)
const itemRaw = ref(props.issue.actionableItem)
const workDomainRaw = ref(props.issue.workDomainData)

const correctiveWorkOnItemForIssue = ref<CorrectiveWorkOnItem | undefined>()

const cardClasses = computed(() => {
  return {
    "sfx-card-issue__overdue": !props.performed && overdue.value,
    "sfx-card-work-order-entity-condensed__performed": props.performed,
    "sfx-card-work-order-entity-condensed__accepted": props.performed && workDone.value,
  }
})

//endregion

//region Functions

async function assignData(issue: IssueCardData) {
  resetData()

  storeIssue.value = cacheStore.issueCard.get(issue.issueData.id)

  correctiveWorkOnItemForIssue.value = await getAcceptedOrUnreviewedCorrectiveWorkOnItemForIssue(
    issue.issueData.id
  )

  assignItem(issue)
  assignWorkDomain(issue)
  iconRow.value = await getIconRow(issue)
  await assignThumbnailAttachment(issue)
}

function resetData() {
  issueThumbnailRaw.value = undefined
  itemRaw.value = undefined
  workDomainRaw.value = undefined
}

function assignItem(issue: IssueCardData) {
  // We already have the data so skip it
  if (itemRaw.value) return

  const item = cacheStore.item.get(issue.issueData.actionable_item)

  itemRaw.value = item

  if (storeIssue.value) storeIssue.value.actionableItem = item
}

function assignWorkDomain(issue: IssueCardData) {
  // We already have the data so skip it
  if (workDomainRaw.value) return

  const workDomain = cacheStore.work_domain_workspace.get(issue.issueData.work_domain)

  workDomainRaw.value = workDomain

  if (storeIssue.value) storeIssue.value.workDomainData = workDomain
}

async function assignThumbnailAttachment(issue: IssueCardData) {
  // We already have the data so skip it
  if (issueThumbnailRaw.value !== undefined) return

  if (issue.issueData.attachments.length > 0) {
    const thumbnail = await getFirstImageAttachment(issue.issueData.attachments)

    if (thumbnail) {
      issueThumbnailRaw.value = thumbnail

      if (storeIssue.value) storeIssue.value.thumbnail = thumbnail
    }
  }
}

async function getIconRow(issue: IssueCardData) {
  // If the issue is NOT performed:

  // From left to right, the icon order is:
  // 1. Issue priority

  // If the issue IS performed:

  // From left to right, the icon order is:
  // 1. Issue priority
  // 2. Corrective Work Icon
  // 3. Tick mark (if work done)

  const iconRow: IconRowElement[] = [
    {
      name: Icons.ISSUE,
      color: Boolean(issue.priorityData) ? getIssuePriorityColor(issue.priorityData!) : undefined,
    },
  ]

  if (props.performed) {
    iconRow.push(
      {
        name: Icons.WORK_CORRECTIVE,
        color: "fun-label-and-icon",
      },
      {
        name: Icons.TICK,
        color: workDone.value ? "positive" : undefined,
      }
    )
  }

  return iconRow
}

function handleClose() {
  emit("clickClose")
}

//endregion

//region Setup Code

watch(
  () => issueThumbnailRaw.value,
  async (thumbnail) => {
    issueThumbnail.value = thumbnail
      ? await getAttachmentContent(thumbnail, "issue_item", props.issue?.issueData.id)
      : undefined
  },
  { immediate: true }
)

watch(
  () => props.issue,
  async (newIssue) => {
    await assignData(newIssue)
  },
  { immediate: true }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_space = _resolveComponent("q-space")!
  const _directive_truncate_middle = _resolveDirective("truncate-middle")!

  return (_openBlock(), _createBlock(SfxCard, _mergeProps({ clickable: "" }, _ctx.$attrs, {
    class: ["sfx-card-work-order-entity-condensed q-my-md", cardClasses.value],
    "icon-row": iconRow.value,
    height: _unref(SFX_CARD_CONDENSED_HEIGHT),
    "img-src": issueThumbnail.value,
    hasImg: ""
  }), {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, _toDisplayString(workDomainDesignation.value), 1),
        (overdue.value || props.closable)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(_component_q_space),
              (overdue.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
                    _createVNode(SfxChip, {
                      label: _ctx.$t('time.generic_overdue'),
                      "icon-right": "warning",
                      color: "negative",
                      "text-color": "white"
                    }, null, 8, ["label"])
                  ]))
                : _createCommentVNode("", true),
              (props.closable)
                ? (_openBlock(), _createBlock(SfxBtnIcon, {
                    key: 1,
                    icon: _unref(Icons).CLOSE,
                    onClick: _withModifiers(handleClose, ["exact","stop"]),
                    padding: "none",
                    class: "sfx-card__top-right-close-button"
                  }, null, 8, ["icon"]))
                : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_4, null, 512), [
        [_directive_truncate_middle, props.issue?.issueData.title]
      ]),
      _createVNode(SfxDataPoint, {
        label: _ctx.$t('sfxCard.item'),
        "card-field": ""
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(itemDesignation.value), 1)
        ]),
        _: 1
      }, 8, ["label"])
    ]),
    _: 1
  }, 16, ["class", "icon-row", "height", "img-src"]))
}
}

})